<template>
  <div class="maintenance-ratings-page">
    <header-portrate />
    <TabsComp :tabs="tabs" />

    <div class="ratings-shell" :style="`direction:${lang.dir || 'ltr'}`">
      <div class="ratings-toolbar">
        <h5>{{ t('maintenance_ratings', 'Maintenance Ratings') }}</h5>
        <div class="toolbar-actions">
          <b-button size="sm" :variant="activeTab === 'report' ? 'primary' : 'outline-primary'" @click="activeTab = 'report'">
            {{ t('rating_report', 'Rating Report') }}
          </b-button>
          <b-button size="sm" :variant="activeTab === 'questions' ? 'primary' : 'outline-primary'" @click="activeTab = 'questions'">
            {{ t('rating_questions', 'Rating Questions') }}
          </b-button>
        </div>
      </div>

      <section v-if="activeTab === 'report'" class="panel">
        <div class="filters">
          <b-form-input v-model="filters.sdate" type="date" />
          <b-form-input v-model="filters.edate" type="date" />
          <b-form-input v-model="filters.cardid" :placeholder="t('cardid', 'Card ID')" />
          <b-form-input v-model="filters.mobile" :placeholder="t('mobile', 'Mobile')" />
          <b-button size="sm" variant="primary" :disabled="loading" @click="loadReport">
            <v-icon small>mdi-magnify</v-icon>
            {{ t('search', 'Search') }}
          </b-button>
        </div>

        <div class="summary-grid">
          <div class="summary-cell">
            <span>{{ t('total', 'Total') }}</span>
            <strong>{{ overall.total || 0 }}</strong>
          </div>
          <div class="summary-cell">
            <span>{{ t('average', 'Average') }}</span>
            <strong>{{ overall.avg_score || 0 }}</strong>
          </div>
          <div v-for="question in summary" :key="question.id" class="summary-cell question-summary">
            <span>{{ questionText(question) }}</span>
            <strong>{{ question.avg_score || 0 }}</strong>
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{{ t('date', 'Date') }}</th>
                <th>{{ t('cardid', 'Card ID') }}</th>
                <th>{{ t('customer_name', 'Customer Name') }}</th>
                <th>{{ t('mobile', 'Mobile') }}</th>
                <th>{{ t('rating', 'Rating') }}</th>
                <th>{{ t('notes', 'Notes') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rating in ratings" :key="rating.id">
                <td>{{ formatDate(rating.created_date) }}</td>
                <td>#{{ rating.job_card_number || rating.cardid }}</td>
                <td>{{ rating.customer_name || '-' }}</td>
                <td>{{ rating.mobile || '-' }}</td>
                <td><span class="score-pill">{{ rating.overall_score || 0 }}</span></td>
                <td>
                  <div>{{ rating.comments || '-' }}</div>
                  <small v-for="answer in rating.answers || []" :key="answer.id">
                    {{ questionText(answer) }}: {{ answer.answer_text || answer.answer_value || '-' }}
                  </small>
                </td>
              </tr>
              <tr v-if="!ratings.length">
                <td colspan="6" class="empty-text">{{ t('no_data', 'No data') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-else class="panel">
        <div class="question-actions">
          <div class="notice" v-if="!canManageQuestions">{{ t('admin_only_questions', 'Only admin users can save rating questions.') }}</div>
          <div>
            <b-button size="sm" variant="outline-primary" @click="addQuestion">
              <v-icon small>mdi-plus</v-icon>
              {{ t('new', 'New') }}
            </b-button>
            <b-button size="sm" variant="success" :disabled="!canManageQuestions || saving" @click="saveQuestions">
              <v-icon small>mdi-content-save-outline</v-icon>
              {{ t('save', 'Save') }}
            </b-button>
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{{ t('question_ar', 'Arabic Question') }}</th>
                <th>{{ t('question_en', 'English Question') }}</th>
                <th>{{ t('type', 'Type') }}</th>
                <th>{{ t('required', 'Required') }}</th>
                <th>{{ t('sort', 'Sort') }}</th>
                <th>{{ t('active', 'Active') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="question in questions" :key="question._key || question.id">
                <td><b-form-input v-model="question.question_ar" :disabled="!canManageQuestions" /></td>
                <td><b-form-input v-model="question.question_en" :disabled="!canManageQuestions" /></td>
                <td><b-form-select v-model="question.input_type" :options="inputTypeOptions" :disabled="!canManageQuestions" /></td>
                <td class="center"><b-form-checkbox v-model="question.is_required" value="1" unchecked-value="0" :disabled="!canManageQuestions" /></td>
                <td><b-form-input v-model="question.sort_order" type="number" :disabled="!canManageQuestions" /></td>
                <td class="center"><b-form-checkbox v-model="question.status" value="1" unchecked-value="0" :disabled="!canManageQuestions" /></td>
              </tr>
              <tr v-if="!questions.length">
                <td colspan="6" class="empty-text">{{ t('no_data', 'No data') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import HeaderPortrate from '@/components/Header-Portrate.vue'
import TabsComp from '@/components/tabsComp.vue'
import { getVisibleTabsForRoute } from '@/menu/menuService'

function isoDate(date) {
  const copy = new Date(date)
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset())
  return copy.toISOString().slice(0, 10)
}

function permissionMatches(pattern, permission) {
  if (!pattern || !permission) return false
  if (pattern === '*' || pattern === permission) return true
  if (pattern.endsWith('.*')) return permission.startsWith(pattern.slice(0, -1))
  return false
}

export default {
  name: 'MaintenanceRatings',
  components: { HeaderPortrate, TabsComp },
  data() {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    return {
      activeTab: 'report',
      loading: false,
      saving: false,
      questions: [],
      ratings: [],
      summary: [],
      overall: {},
      filters: {
        sdate: isoDate(start),
        edate: isoDate(now),
        cardid: '',
        mobile: '',
      },
    }
  },
  computed: {
    lang() {
      if (this.$cookies.get(this.$store.state.LangCooki) != null) {
        if (this.$cookies.get(this.$store.state.LangCooki) == 'en') return this.$store.state.lang.en
        if (this.$cookies.get(this.$store.state.LangCooki) == 'ur') return this.$store.state.lang.ur
        return this.$store.state.lang.ar
      }
      if (this.$store.state.deflang == 'en') return this.$store.state.lang.en
      if (this.$store.state.deflang == 'ur') return this.$store.state.lang.ur
      return this.$store.state.lang.ar
    },
    tabs() {
      return getVisibleTabsForRoute(this.$store.getters['state'], this.lang, this.$route.path)
    },
    inputTypeOptions() {
      return [
        { text: this.t('rating', 'Rating'), value: 'rating' },
        { text: this.t('yes_no', 'Yes / No'), value: 'yes_no' },
        { text: this.t('text', 'Text'), value: 'text' },
      ]
    },
    canManageQuestions() {
      const mode = String(this.$store.state.permissionMode || 'legacy')
      if (mode === 'admin' || mode === 'legacy') return true
      const denied = Array.isArray(this.$store.state.deniedPermissions) ? this.$store.state.deniedPermissions : []
      if (denied.some((permission) => permissionMatches(permission, 'settings.manage'))) return false
      const allowed = Array.isArray(this.$store.state.userPermissions) ? this.$store.state.userPermissions : []
      return allowed.some((permission) => permissionMatches(permission, 'settings.manage'))
    },
  },
  created() {
    this.loadQuestions()
    this.loadReport()
  },
  methods: {
    t(key, fallback) {
      return (this.lang && this.lang[key]) || fallback
    },
    async postApi(type, payload = {}) {
      const post = new FormData()
      post.append('defBranch', localStorage.getItem('defBranch') || '1')
      post.append('financeYear', localStorage.getItem('financeYear') || '')
      post.append('type', type)
      post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase))
      post.append('data[_]', '1')
      Object.keys(payload).forEach((key) => {
        const value = payload[key]
        post.append(`data[${key}]`, typeof value === 'object' ? JSON.stringify(value) : value)
      })
      const response = await fetch(this.$store.state.SAMCOTEC.r_path, { method: 'POST', body: post })
      const body = await response.text()
      if (!body.trim()) return { error: { number: response.status || 500, message: 'Empty API response' }, results: { data: [] } }
      try {
        return JSON.parse(body)
      } catch (_error) {
        return { error: { number: response.status || 500, message: 'Invalid API response' }, results: { data: [] } }
      }
    },
    async loadQuestions() {
      const res = await this.postApi('getMaintenanceRatingQuestions', { id: 'all' })
      if (res && res.error && Number(res.error.number) === 200) {
        this.questions = (res.results.data || []).map((question) => ({
          ...question,
          is_required: String(question.is_required == null ? 1 : question.is_required),
          status: String(question.status == null ? 1 : question.status),
        }))
      }
    },
    async saveQuestions() {
      if (!this.canManageQuestions) return
      this.saving = true
      try {
        const res = await this.postApi('saveMaintenanceRatingQuestions', { questions: this.questions })
        if (res && res.error && Number(res.error.number) === 200) {
          this.questions = (res.results.data || []).map((question) => ({
            ...question,
            is_required: String(question.is_required == null ? 1 : question.is_required),
            status: String(question.status == null ? 1 : question.status),
          }))
        } else {
          window.alert((res && res.error && res.error.message) || 'Error')
        }
      } finally {
        this.saving = false
      }
    },
    async loadReport() {
      this.loading = true
      try {
        const res = await this.postApi('getMaintenanceRatingReport', this.filters)
        if (res && res.error && Number(res.error.number) === 200) {
          this.ratings = res.results.data || []
          this.summary = res.results.summary || []
          this.overall = res.results.overall || {}
        } else {
          window.alert((res && res.error && res.error.message) || 'Error')
        }
      } finally {
        this.loading = false
      }
    },
    addQuestion() {
      const nextSort = (this.questions.length + 1) * 10
      this.questions.push({
        _key: `new-${Date.now()}`,
        id: 0,
        question_ar: '',
        question_en: '',
        input_type: 'rating',
        is_required: '1',
        sort_order: nextSort,
        status: '1',
      })
    },
    questionText(question) {
      return this.lang.langname === 'en'
        ? (question.question_en || question.question_ar)
        : (question.question_ar || question.question_en)
    },
    formatDate(value) {
      if (!value) return '-'
      return String(value).slice(0, 16)
    },
  },
}
</script>

<style scoped>
.ratings-shell {
  padding: 14px 18px 24px;
  background: #f5f6f8;
  min-height: calc(100vh - 120px);
}
.ratings-toolbar,
.panel {
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
}
.ratings-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.ratings-toolbar h5 {
  margin: 0;
  font-weight: 800;
}
.toolbar-actions,
.filters,
.question-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.panel {
  background: #ffffff;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  padding: 14px;
}
.filters {
  margin-bottom: 14px;
}
.filters .form-control {
  max-width: 170px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.summary-cell {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 6px;
  padding: 10px;
}
.summary-cell span {
  display: block;
  color: #667085;
  font-size: 0.82rem;
  min-height: 22px;
}
.summary-cell strong {
  font-size: 1.3rem;
}
.question-summary span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.table-wrap {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  padding: 9px;
  border-bottom: 1px solid #edf0f4;
  text-align: start;
  vertical-align: top;
}
th {
  background: #1f2937;
  color: #fff;
}
td small {
  display: block;
  color: #667085;
  margin-top: 4px;
}
.score-pill {
  display: inline-flex;
  min-width: 38px;
  justify-content: center;
  background: #fff3cd;
  border: 1px solid #ffe69c;
  border-radius: 999px;
  padding: 3px 8px;
  font-weight: 800;
}
.question-actions {
  justify-content: space-between;
  margin-bottom: 12px;
}
.notice {
  background: #fff3cd;
  border: 1px solid #ffe69c;
  border-radius: 6px;
  padding: 8px 10px;
}
.center {
  text-align: center;
}
.empty-text {
  text-align: center;
  color: #667085;
}
@media (max-width: 900px) {
  .ratings-toolbar,
  .question-actions {
    align-items: stretch;
    flex-direction: column;
  }
  .summary-grid {
    grid-template-columns: 1fr;
  }
  .filters .form-control {
    max-width: none;
  }
}
</style>
